import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useCourse } from '../context/CourseContext';
import { ProgressTracker } from '../components/course/ProgressTracker';
import { Button } from '../components/ui/button';
import {
  Award,
  ArrowRight,
  RotateCcw,
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';

export const ProgressPage = () => {
  const { overallProgress, resetProgress, chapters, getChapterProgress } = useCourse();

  // Find the next chapter to continue
  const nextChapter = chapters.find(ch => !getChapterProgress(ch.id).completed);

  const handleReset = () => {
    resetProgress();
    toast.success('Progress reset successfully', {
      description: 'You can start fresh on your journey.',
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-silver-shimmer/10 py-12">
      <div className="container mx-auto px-4">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-2xl mx-auto text-center mb-12"
        >
          <div className="w-16 h-16 rounded-full bg-maroon/10 flex items-center justify-center mx-auto mb-6 shadow-maroon">
            <Award className="w-8 h-8 text-maroon" />
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-foreground mb-4">
            Your Progress
          </h1>
          <p className="text-muted-foreground text-lg">
            Track your transformation journey through the 5 steps
          </p>
        </motion.div>

        {/* Progress Tracker */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="max-w-2xl mx-auto mb-12"
        >
          <ProgressTracker />
        </motion.div>

        {/* Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="max-w-2xl mx-auto"
        >
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {nextChapter && overallProgress < 100 && (
              <Link to={`/chapter/${nextChapter.id}`}>
                <Button size="lg" className="btn-teal gap-2 w-full sm:w-auto">
                  Continue Learning
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            )}

            {overallProgress === 100 && (
              <Link to="/chapters">
                <Button size="lg" className="btn-gold gap-2 w-full sm:w-auto">
                  Review Chapters
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            )}

            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" size="lg" className="gap-2">
                  <RotateCcw className="w-4 h-4" />
                  Reset Progress
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Reset All Progress?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will clear all your reading progress, journal entries, and bookmarks. 
                    This action cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handleReset} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                    Reset Everything
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </motion.div>

        {/* Motivational Quote */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="max-w-xl mx-auto text-center mt-16 p-8 bg-gradient-to-br from-silver-shimmer/20 to-background rounded-xl border border-silver/20 shadow-silver"
        >
          <blockquote className="font-display text-xl italic text-foreground mb-4">
            "You're not broken, and you never were. You're a magnificent being 
            learning to remember your power."
          </blockquote>
          <cite className="text-maroon font-medium">— Christopher M Duncan</cite>
        </motion.div>
      </div>
    </div>
  );
};

export default ProgressPage;
